import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';
import authService from '../auth/authService';
import { API_CONFIG } from '../config/api.config';

export interface OperationalSummary {
  studiesReceived: number;
  inProgress: number;
  awaitingReview: number;
  completed: number;
  pending: number;
  overdue: number;
  cancelled: number;
  avgReportingTimeMinutes: number;
  tatCompliancePercent: number;
}

export interface OperationalTat {
  averageMinutes: number;
  medianMinutes: number;
  withinSla: number;
  exceedingSla: number;
  compliancePercent: number;
  notConfigured: number;
}

export interface WorkloadTrendPoint {
  date: string;
  received: number;
  completed: number;
  pending: number;
}

export interface OperationalStudyRow {
  accession: string;
  patient: string;
  modality: string;
  department: string;
  radiologist: string;
  priority: string;
  status: string;
  modifiedUser?: string;
  tatMinutes: number | null;
  tatExceeded: boolean;
  overdue?: boolean;
  tatNotConfigured?: boolean;
}

export interface OperationalDashboardData {
  summary: OperationalSummary;
  tat: OperationalTat;
  workloadTrend: WorkloadTrendPoint[];
  studies: {
    data: OperationalStudyRow[];
    pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
  };
  filters: { facilityName: string; lastRefreshedAt: string; scopeType?: string };
  filterOptions?: OperationalFilterOptions;
}

export interface OperationalQuery {
  from?: string;
  to?: string;
  facilityId?: string;
  modality?: string;
  department?: string;
  radiologistId?: string;
  priority?: string;
  patientType?: string;
  status?: string;
  workloadPeriod?: string;
  page?: number;
  pageSize?: number;
}

export interface OperationalFilterOptions {
  modalities: string[];
  departments: string[];
  radiologists: Array<{ id: number; name: string }>;
  facilities?: Array<{ id: number; name: string }>;
  studyStatuses: string[];
  priorities: string[];
  patientTypes: string[];
}

function normalizeStudyRow(row: Record<string, unknown>): OperationalStudyRow {
  const text = (value: unknown, fallback = '—') => {
    if (value == null) {
      return fallback;
    }
    const s = String(value).trim();
    return s || fallback;
  };

  return {
    accession: text(row.accession ?? row.accession_number ?? row.res_visit_it),
    patient: text(row.patient ?? row.patient_name),
    modality: text(row.modality),
    department: text(row.department ?? row.department_name, 'Radiology'),
    radiologist: text(row.radiologist ?? row.referring_doctor_name, 'Unassigned'),
    priority: text(row.priority, 'Routine'),
    status: text(row.status),
    modifiedUser: text(row.modifiedUser ?? row.updated_by ?? row.modified_user),
    tatMinutes:
      row.tatMinutes != null
        ? Number(row.tatMinutes)
        : row.tat_minutes != null
          ? Number(row.tat_minutes)
          : null,
    tatExceeded: Boolean(row.tatExceeded ?? row.tat_exceeded),
    overdue: Boolean(row.overdue),
    tatNotConfigured: Boolean(row.tatNotConfigured ?? row.tat_not_configured),
  };
}

function normalizeOperationalData(data: OperationalDashboardData): OperationalDashboardData {
  return {
    ...data,
    studies: {
      ...data.studies,
      data: (data.studies?.data || []).map(row =>
        normalizeStudyRow(row as unknown as Record<string, unknown>)
      ),
    },
  };
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '' && String(value) !== 'all') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getOperationalDashboard(
  query: OperationalQuery
): Promise<{ success: boolean; data?: OperationalDashboardData; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.ANALYTICS.OPERATIONAL}${buildQuery(query as Record<string, string | number | undefined>)}`;
    const response = await apiGet<{ success: boolean; data: OperationalDashboardData }>(url, {
      requireAuth: true,
    });
    if (response.data?.success && response.data.data) {
      return { success: true, data: normalizeOperationalData(response.data.data) };
    }
    return { success: false, error: 'Failed to load operational dashboard' };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load operational dashboard',
    };
  }
}

export async function exportOperationalDashboard(
  query: OperationalQuery,
  format: 'csv' | 'pdf' | 'excel' = 'csv'
): Promise<void> {
  const url = `${API_ENDPOINTS.ANALYTICS.OPERATIONAL_EXPORT}${buildQuery({
    ...query,
    format: format === 'excel' ? 'excel' : format,
    page: 1,
    pageSize: 100000,
  } as Record<string, string | number | undefined>)}`;
  const authHeader = authService.getAuthHeader();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'text/csv, application/vnd.ms-excel, application/json',
      'tenant-id': API_CONFIG.TENANT_ID,
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {}),
    },
    mode: 'cors',
  });
  if (response.status === 202) {
    const body = await response.json();
    const jobId = body.jobId;
    if (jobId) {
      globalThis.alert(
        `Export started (${body.recordCount} records). Job #${jobId} — you will be notified when ready.`
      );
    }
    return;
  }
  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const ext = format === 'excel' ? 'xls' : 'csv';
  const filename = match?.[1] || `OperationalDashboard_${Date.now()}.${ext}`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
